import { DEFAULT_USER_DETAILS } from '@common/constants'
import type { AuthPhoneCreateData } from '@lib/api/human'
import { decodePhoneQueryValue } from '@lib/utils/phone-query'
import React from 'react'

export type IdentifierMode = 'phone' | 'email'

/**
 * Request body for `POST /api/v1/auth/phone/`, taken from the generated schema
 * rather than restated by hand - so a serializer change shows up as a type
 * error here instead of a runtime surprise.
 */
export type OtpRequestBody = NonNullable<AuthPhoneCreateData['body']>

// Mirrors `human.constants.PLACEHOLDER_PHONE_PREFIX`. Signups that identified
// themselves with an email get a synthetic number on this prefix instead of a
// real one, and we ask for the real number once a trial is booked.
export const PLACEHOLDER_PHONE_PREFIX = '+8811'

export const isPlaceholderPhone = (phone?: string | null): boolean =>
  Boolean(phone) && phone.startsWith(PLACEHOLDER_PHONE_PREFIX)

// The address `processor_user_email_validator` writes when Kickbox says the
// one we hold is undeliverable. Its presence is the live signal that we still
// cannot email this account - `rejectedEmail` only says we could not once,
// and it is kept forever as a record of what the parent typed.
export const isDummyEmail = (email?: string | null): boolean =>
  Boolean(email) && email.endsWith(DEFAULT_USER_DETAILS.emailDomain)

// Anything that is not part of a typed phone number means the user is entering
// an email. Kept deliberately loose: this only picks which input widget and
// which request field to use, the real validation happens separately.
const NON_PHONE_CHARACTER = /[^\d\s()+-]/

export const detectIdentifierMode = (value: string): IdentifierMode =>
  NON_PHONE_CHARACTER.test(value || '') ? 'email' : 'phone'

// True when `?ph=` carries an email a landing page already accepted. The
// register page honours it whatever its own country check says, or the signup
// the landing page forwards would be forced into phone mode and break.
export const isEmailPhoneQuery = (value?: string | string[] | null): boolean =>
  detectIdentifierMode(decodePhoneQueryValue(value)) === 'email'

/**
 * Normalise a typed number into the value the phone widget expects.
 *
 * The widget's own convention is bare digits with the dial code in front and
 * no plus sign, and `buildIdentifierBody` adds the plus back at submit time.
 * Returning anything else leaves the form holding a string it cannot send:
 * an autofilled "+91 77777 77771" went out as "++91 77777 77771" and was
 * rejected, while the field looked perfectly correct, because the widget
 * renders what it is given without reporting a change back.
 *
 * A value that opens with "+" already names its own country code, so it keeps
 * it rather than being given ours - prefixing one turned that same number
 * into "+91 91777 77777", a plausible number belonging to somebody else.
 *
 * Numbers whose own leading digits happen to match the dial code are read as
 * already prefixed. That is wrong for a national number opening with its
 * country's own code, but it is the existing behaviour and telling the two
 * apart needs more than a prefix test.
 */
export const applyDialCode = (value: string, dialCode: string): string => {
  const trimmed = (value || '').trim()
  const digits = trimmed.replace(/\D/g, '')
  if (!digits) return value

  const carriesOwnDialCode =
    trimmed.startsWith('+') || digits.startsWith(dialCode)
  return !dialCode || carriesOwnDialCode ? digits : `${dialCode}${digits}`
}

/**
 * Build the request field for an identifier the user typed.
 *
 * The API accepts either `phone` or `email`, never both. Phone values come out
 * of `PhoneInput` as digits with the dial code and no plus sign, so they need
 * one adding.
 */
export const buildIdentifierBody = (
  value: string,
  mode: IdentifierMode
): { phone: string } | { email: string } =>
  mode === 'email' ? { email: value.trim() } : { phone: `+${value}` }

/**
 * Event handlers that turn a phone-only `react-phone-input-2` into the phone
 * half of a combined phone-or-email field.
 *
 * The widget silently drops non-numeric input, so the value alone never tells
 * us the user meant to type an email. These watch the raw key and paste events
 * instead and hand the caller the first email-ish character.
 */
export const createIdentifierSwapHandlers = (
  onChange: (value: string, mode: IdentifierMode) => void,
  onEnter?: () => void,
  /**
   * What the user actually typed, without the dial code the widget prepends.
   * Carried across the swap so typing "1990s" keeps the 1990.
   */
  getTypedDigits: () => string = () => ''
) => ({
  onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onEnter?.()
      return
    }
    const isPrintableCharacter = event.key.length === 1
    if (!isPrintableCharacter || event.metaKey || event.ctrlKey) return
    if (detectIdentifierMode(event.key) !== 'email') return

    event.preventDefault()
    // Keep what they typed. The widget's own value carries the dial code, so
    // appending to that would yield "911990s" - hence the digits come back
    // from the caller with the dial code already stripped.
    onChange(`${getTypedDigits()}${event.key}`, 'email')
  },
  onPaste: (event: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = event.clipboardData.getData('text')
    if (detectIdentifierMode(pasted) !== 'email') return

    event.preventDefault()
    // Replaces rather than appends: pasting an address means that address,
    // where a single keystroke means they are still typing one.
    onChange(pasted.trim(), 'email')
  },
})
